import { api, endpoint } from "@/api";
import BusinessSerializeSelector from "@/components/BusinessSerializeSelector";
import { flare } from "@flareapp/js";
import { App, Button, Form, Input, Select, Switch } from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";

const onFinishFailed = (errorInfo) => {
  // eslint-disable-next-line no-console
  console.log("Failed:", errorInfo);
};

function MetaDashboardModal({ show, setShow, mode = "create", data = {}, setData, getDashboard }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state.user);
  const { notification } = App.useApp();

  //   state
  const [selectedBusiness, setSelectedBusiness] = useState([]);
  const [isLimitOver, setIsLimitOver] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");

  const { business, site, hasTabs, name, tabNumber = 4, isPrivate = false, isDefault = false, type } = data;
  const watchedHasTabs = Form.useWatch("hasTabs", form);
  const showTabsInRow = watchedHasTabs !== undefined ? Boolean(watchedHasTabs) : Boolean(hasTabs);

  useEffect(() => {
    if (!show) return;
    if (mode === "edit" || mode === "copy") {
      const normBus = Array.isArray(business)
        ? business.map((b) => (typeof b === "object" ? b?._id || b?.id : b)).filter(Boolean)
        : typeof business === "object"
        ? business?._id || business?.id
        : business;

      const normSite = Array.isArray(site)
        ? site.map((s) => (typeof s === "object" ? s?._id || s?.id : s)).filter(Boolean)
        : typeof site === "object"
        ? site?._id || site?.id
        : site;

      form.setFieldsValue({
        business: normBus,
        site: normSite,
        name,
        hasTabs,
        tabNumber,
        isPrivate,
        isDefault,
        type: type || "tile",
      });
      setSelectedBusiness(normBus);
    } else if (mode === "create") {
      form.resetFields();
      form.setFieldsValue({ type: "tile", tabNumber: 4 });
      setSelectedBusiness([]);
    }
  }, [show, mode, data?._id]);

  //   fetch subscriptions
  const getMySubscription = async () => {
    try {
      const { hasAccess, assignedPlan } = await api.get(endpoint.mySubscription, {
        params: {
          type: "dashboard",
          unknown: true,
        },
      });
      if (mode !== "edit") {
        setIsLimitOver(!hasAccess);
        if (!hasAccess) {
          setWarnMsg(
            `${assignedPlan?.business?.name} (${assignedPlan?.business?.id})` + " has exceeded the limit for the maximum number of Dashboard.",
          );
        }
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // active dashboard
  const getActiveDashboard = async () => {
    try {
      const { result } = await api.get(endpoint.active_dashboard);
      form.setFieldsValue({ default: result === data?._id });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("active dashboard", error);
    }
  };

  //   effects
  useEffect(() => {
    getActiveDashboard();
  }, []);

  useEffect(() => {
    getMySubscription();
  }, [selectedBusiness]);

  const handleClose = () => {
    setShow(false);
    form.setFieldsValue({});
  };

  const handleUpdate = async (body) => {
    try {
      const response = await axios.put(endpoint.update_dashboard + "/" + data._id, body, { withCredentials: true });
      if (response.status === 204) {
        // Swal.fire("", "Dashboard Updated Successfully!", "success");
        notification.success({ key: "dashboard-save-notification", message: `Dashboard Updated Successfully!.`, duration: 2, showProgress: true });
        // navigate("/dashboard-list");
        getDashboard();
        handleClose();
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const handleCreate = async (body) => {
    try {
      const { data } = await axios.post(endpoint.create_dashboard, body, { withCredentials: true });

      if (data?.success) {
        notification.success({
          key: "dashboard-save-notification",
          message: "Dashboard Created Successfully!",
          duration: 2,
          showProgress: true,
        });
        handleClose();
        navigate(`/edit-dashboard/${data?.result?._id}`);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const onFinish = async (values) => {
    const dashboardType = uuid();
    const body = {
      ...values,
      dashboardType,
      // isPrivate: userDetails?.role === userRole.superAdmin ? false : Boolean(values.isPrivate),
    };
    if (mode === "copy") {
      setData((pre) => ({ ...pre, ...values }));
      setShow(false);
    } else if (mode === "create") {
      handleCreate(body);
    } else {
      handleUpdate(body);
    }
  };

  return (
    <div>
      <Modal show={show} onHide={handleClose} backdrop="static" centered size="lg" backdropClassName="modal-bg-1">
        {/* header */}
        <Modal.Header closeButton className="bg-gray-200 border-dark-subtle">
          <Modal.Title>{mode === "create" ? "Add Dashboard" : mode === "edit" ? "Edit Dashboard" : "Set New Dashboard Details"}</Modal.Title>
        </Modal.Header>
        {/* body */}
        <Modal.Body className="p-3">
          <Form name="createDashboard" form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off" layout="vertical">
            <Row lg={2}>
              <Col>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: "Input Dashboard Name" }]}>
                  <Input placeholder="Enter Dashboard Name" />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item label="Dashboard Type" name="type" rules={[{ required: true, message: "Input Dashboard Type" }]}>
                  <Select
                    disabled={mode === "edit"}
                    placeholder="Select Dashboard Type"
                    options={[
                      { label: "Tile", value: "tile" },
                      { label: "Live", value: "live" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row lg={2}>
              <BusinessSerializeSelector
                {...{
                  business: {
                    extra: warnMsg && <p style={{ color: "red", marginBottom: 0, marginTop: "2px" }}>Warning: {warnMsg}</p>,
                  },
                  device: { show: false },
                  parameter: { show: false },
                  form,
                }}
              />
            </Row>

            <Form.Item layout="horizontal" label="Default Dashboard" name="isDefault" colon={false} className="right-label mb-0">
              <Switch />
            </Form.Item>
            {mode === "edit" && (
              <>
                <Form.Item layout="horizontal" label="Dashboard Tabs" name="hasTabs" colon={false} className="right-label mb-0">
                  <Switch
                    checked={showTabsInRow}
                    onChange={(value) => {
                      form.setFieldValue("hasTabs", value);
                      if (typeof setData === "function") {
                        setData((pre) => ({ ...pre, hasTabs: value }));
                      }
                    }}
                  />
                </Form.Item>

                {showTabsInRow && (
                  <Row>
                    <Col md={6}>
                      <Form.Item
                        initialValue={4}
                        rules={[{ required: true, message: "Enter Tabs Numbers In 1-10" }]}
                        label="Tabs In A Row"
                        name="tabNumber"
                      >
                        <Select options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ label: n, value: n }))} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {userDetails.role === "superAdmin" && mode === "create" ? (
              ""
            ) : (
              <Form.Item layout="horizontal" label="Make It Private" name="isPrivate" colon={false} className="right-label mb-0">
                <Switch />
              </Form.Item>
            )}

            <div className="d-flex align-items-center justify-content-end gap-3">
              <Button disabled={isLimitOver} type="primary" htmlType="submit">
                {mode === "create" ? "Create Dashboard" : mode === "edit" ? "Update Dashboard" : "Save"}
              </Button>
              <Button type="default" onClick={handleClose} danger htmlType="button">
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MetaDashboardModal;

MetaDashboardModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func,
  mode: PropTypes.string,
  data: PropTypes.object,
  setData: PropTypes.func,
  getDashboard: PropTypes.func,
};
